import React from 'react';
import QuoteItemCard from './QuoteItemCard';
import './QuoteItemsSection.css'

const QuoteItemsSection = ({
  items,
  availableItems,
  taxType,
  hasdiscount,
  isMobile,
  onProductSelect,
  onItemChange,
  onAddItem,
  onRemoveItem,
  getItemTotal,
  calculateItemTax
}) => {
  // Calculate columns dynamically - show subtotal only when there's NO discount
  const hasSubtotal = taxType !== 'NONE' && !hasdiscount;
  const hasTaxColumn = taxType !== 'NONE' && !hasdiscount;
  const commonTableCols = (hasSubtotal ? 1 : 0) + (hasTaxColumn ? 1 : 0) + 4; // Product, Qty, Unit Price, Total

  // Function to calculate total based on discount presence
  const calculateFinalTotal = (item) => {
    if (hasdiscount) {
      // If there's discount: total = unit price * quantity (no tax)
      return (item.unit_price || 0) * (item.quantity || 0);
    } else {
      // If no discount: total = subtotal + tax
      return getItemTotal(item) + calculateItemTax(item);
    }
  };

  return (
    <section className="QuoteItemsSection__section">
      <h2 className="QuoteItemsSection__section-title">Items</h2>
      
      {/* Mobile Cards - Always rendered, visibility controlled by CSS */}
      <div className="QuoteItemsSection__items-mobile-cards">
        {items.map((item, i) => (
          <QuoteItemCard
            key={i}
            item={item}
            index={i}
            availableItems={availableItems}
            taxType={taxType}
            hasdiscount={hasdiscount}
            onProductSelect={onProductSelect}
            onItemChange={onItemChange}
            onRemoveItem={onRemoveItem}
            getItemTotal={getItemTotal}
            calculateItemTax={calculateItemTax}
          />
        ))}
        
        <button 
          onClick={onAddItem} 
          className="QuoteItemsSection__button QuoteItemsSection__button--add"
        >
          + Add Item
        </button>
      </div>
      
      {/* Desktop Table - Always rendered, visibility controlled by CSS */}
      <div className="QuoteItemsSection__items-table-container">
        <table className="QuoteItemsSection__items-table">
          <thead>
            <tr>
              <th className="QuoteItemsSection__table-header">Product</th>
              <th className="QuoteItemsSection__table-header">Qty</th>
              <th className="QuoteItemsSection__table-header">Unit Price</th>
              {hasSubtotal && <th className="QuoteItemsSection__table-header">Subtotal</th>}
              {hasTaxColumn && <th className="QuoteItemsSection__table-header">Tax</th>}
              <th className="QuoteItemsSection__table-header">Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <React.Fragment key={i}>
                <tr className="QuoteItemsSection__table-row">
                  <td className="QuoteItemsSection__table-cell QuoteItemsSection__table-cell--product">
                    <div className="QuoteItemsSection__product-with-delete">
                      <ProductInputDropdown
                        item={item}
                        index={i}
                        availableItems={availableItems}
                        onProductSelect={onProductSelect}
                        onItemChange={onItemChange}
                      />
                      <button 
                        className="QuoteItemsSection__delete-icon" 
                        onClick={() => onRemoveItem(i)}
                        title="Remove item"
                      >
                        <svg 
                          width="14" 
                          height="14" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path 
                            d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2-2V6M10 11v6M14 11v6" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                  
                  <td className="QuoteItemsSection__table-cell">
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="1"
                      className="QuoteItemsSection__table-input QuoteItemsSection__table-input--number QuoteItemsSection__table-input--no-spinner"
                      value={item.quantity || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 1)) {
                          const numValue = value === '' ? '' : Math.max(1, parseInt(value) || 1);
                          onItemChange(i, 'quantity', numValue);
                        }
                      }}
                    />
                  </td>
                  
                  <td className="QuoteItemsSection__table-cell">
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="0"
                      className="QuoteItemsSection__table-input QuoteItemsSection__table-input--number QuoteItemsSection__table-input--no-spinner"
                      value={item.unit_price || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || (/^\d*\.?\d*$/.test(value) && parseFloat(value) >= 0)) {
                          const numValue = value === '' ? 0 : Math.max(0, parseFloat(value) || 0);
                          onItemChange(i, 'unit_price', numValue);
                        }
                      }}
                    />
                  </td>
                  
                  {hasSubtotal && (
                    <td className="QuoteItemsSection__table-cell">
                      <span className="QuoteItemsSection__table-amount" title={`₹${getItemTotal(item).toFixed(2)}`}>
                        ₹{getItemTotal(item).toFixed(2)}
                      </span>
                    </td>
                  )}
                  
                  {hasTaxColumn && (
                    <td className="QuoteItemsSection__table-cell">
                      <span className="QuoteItemsSection__table-amount" title={`₹${calculateItemTax(item).toFixed(2)}`}>
                        ₹{calculateItemTax(item).toFixed(2)}
                      </span>
                    </td>
                  )}
                  
                  <td className="QuoteItemsSection__table-cell">
                    <span className="QuoteItemsSection__table-amount QuoteItemsSection__table-amount--total" title={`₹${calculateFinalTotal(item).toFixed(2)}`}>
                      ₹{calculateFinalTotal(item).toFixed(2)}
                    </span>
                  </td>
                </tr>
                
                <tr className="QuoteItemsSection__table-row QuoteItemsSection__table-row--description">
                  <td 
                    className="QuoteItemsSection__table-cell QuoteItemsSection__table-cell--description" 
                    colSpan={commonTableCols}
                  >
                    <textarea
                      placeholder="Item description (optional)..."
                      className="QuoteItemsSection__table-textarea QuoteItemsSection__table-textarea--description"
                      value={item.description}
                      onChange={(e) => onItemChange(i, 'description', e.target.value)}
                    />
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
        
        <button 
          onClick={onAddItem} 
          className="QuoteItemsSection__button QuoteItemsSection__button--add"
        >+ Add Item</button>
      </div>
    </section>
  );
};

// Custom Product Input Dropdown Component
const ProductInputDropdown = ({ item, index, availableItems, onProductSelect, onItemChange }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState(item.product_name || '');
  const [isSearching, setIsSearching] = React.useState(false);
  const dropdownRef = React.useRef(null);

  // Sync searchTerm with item.product_name when it changes externally
  React.useEffect(() => {
    setSearchTerm(item.product_name || '');
  }, [item.product_name]);

  // Get filtered items - show all when not searching, filter when typing
  const filteredItems = isSearching 
    ? availableItems.filter(product =>
        product.product_name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : availableItems;

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsSearching(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    onItemChange(index, 'product_name', value);
    
    // Enable search mode and open dropdown when typing
    setIsSearching(true);
    setIsOpen(true);
  };

  const handleProductSelect = (product) => {
    setSearchTerm(product.product_name);
    onProductSelect(index, product.product_id);
    setIsOpen(false);
    setIsSearching(false);
  };

  const handleArrowClick = (e) => {
    e.preventDefault();
    setIsSearching(false); // Disable search mode to show all items
    setIsOpen(!isOpen);
  };

  const handleInputFocus = () => {
    // Show all items when focusing (not in search mode)
    setIsSearching(false);
    setIsOpen(true);
  };

  const handleInputClick = () => {
    // Show all items on click (not in search mode)
    setIsSearching(false);
    setIsOpen(true);
  };

  return (
    <div className="QuoteItemsSection__product-dropdown" ref={dropdownRef}>
      <div className="QuoteItemsSection__product-input-container">
        <input
          type="text"
          placeholder="Enter product name or select from list"
          className="QuoteItemsSection__table-input QuoteItemsSection__table-input--product"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onClick={handleInputClick}
        />
        <button
          type="button"
          className="QuoteItemsSection__dropdown-arrow"
          onClick={handleArrowClick}
        >
          <svg
            className={`QuoteItemsSection__arrow-icon ${isOpen ? 'QuoteItemsSection__arrow-icon--open' : ''}`}
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 4.5L6 7.5L9 4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      
      {isOpen && filteredItems.length > 0 && (
        <div className="QuoteItemsSection__dropdown-menu">
          {filteredItems.map((product) => (
            <div
              key={product.product_id}
              className="QuoteItemsSection__dropdown-item"
              onClick={() => handleProductSelect(product)}
            >
              <div className="QuoteItemsSection__dropdown-item-name">
                {product.product_name}
              </div>
              <div className="QuoteItemsSection__dropdown-item-price">
                ₹{product.price_selling}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {searchTerm && filteredItems.length === 0 && !isOpen && (
        <div className="QuoteItemsSection__input-helper">
          <small className="QuoteItemsSection__helper-text">
            ✓ Custom product name will be used
          </small>
        </div>
      )}
    </div>
  );
};

export default QuoteItemsSection;